import apiClient from "../helpers/api_client";
import { getToken } from "./AuthToken";
import { getApiBaseUrl, getBackendOrigin, resolveBackendUrl } from "../helpers/serverUrl";

const barcodeRequestConfig = {
  suppressGlobalError: true,
};

const logBarcodeRequest = (method, url, payload) => {
  if (typeof window === "undefined") return;
  const resolvedUrl = resolveBarcodeUrl(url);
  if (payload !== undefined) {
    console.log(`[Barcode API] ${method} ${resolvedUrl}`, payload);
    return;
  }
  console.log(`[Barcode API] ${method} ${resolvedUrl}`);
};

export const BARCODE_CATEGORY_OPTIONS = [
  { code: "LAND", label: "Land" },
  { code: "BLDG", label: "Building" },
  { code: "FURN", label: "Furniture" },
  { code: "ICT", label: "ICT" },
  { code: "VEH", label: "Vehicle" },
  { code: "MACH", label: "Machine" },
  { code: "EQP", label: "Equipment" },
  { code: "SITE", label: "Site Works" },
  { code: "INFR", label: "Infrastructure" },
];

export const BARCODE_REVIEW_SCOPE_OPTIONS = [
  { value: "reviewed", label: "Reviewed" },
  { value: "all", label: "All" },
  { value: "unreviewed", label: "Unreviewed" },
];

export const resolveBarcodeUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${getBackendOrigin()}${url}`;
  if (url.startsWith("/")) return resolveBackendUrl(url);
  return resolveBackendUrl(url);
};

export const generateBarcodes = (payload) => {
  logBarcodeRequest("POST", "/barcodes/generate", payload);
  return apiClient.post("/barcodes/generate", payload, barcodeRequestConfig);
};

export const previewBarcodeGenerationCount = (payload) => {
  logBarcodeRequest("POST", "/barcodes/generate/preview-count", payload);
  return apiClient.post("/barcodes/generate/preview-count", payload, barcodeRequestConfig);
};

export const getBarcodePdfGroups = (params = {}) => {
  logBarcodeRequest("GET", "/barcodes/pdf-groups", params);
  return apiClient.get("/barcodes/pdf-groups", {
    ...barcodeRequestConfig,
    params,
  });
};



export const generateAssetBarcode = (assetId) => {
  logBarcodeRequest("POST", `/assets/${assetId}/generate-barcode`);
  return apiClient.post(`/assets/${assetId}/generate-barcode`, null, barcodeRequestConfig);
};

export const checkAssetBarcodeExists = (barcode) => {
  logBarcodeRequest("GET", `/assets/exists/barcode/${barcode}`);
  return apiClient.get(`/assets/exists/barcode/${barcode}`, barcodeRequestConfig);
};

export const getAssetByBarcode = (barcode) => {
  logBarcodeRequest("GET", `/assets/by-barcode/${barcode}`);
  return apiClient.get(`/assets/by-barcode/${barcode}`, barcodeRequestConfig);
};

export const getBarcodeExportUrl = (extension, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== "") {
      query.append(key, val);
    }
  });
  const queryString = query.toString();
  return `${getApiBaseUrl()}/assets/barcodes/export.${extension}${queryString ? "?" + queryString : ""}`;
};

export const getBarcodeScanUrl = (barcode) =>
  `${getApiBaseUrl()}/assets/scan/${encodeURIComponent(barcode)}`;

export const getBarcodeQueryScanUrl = (barcode) =>
  `${getApiBaseUrl()}/assets/scan?code=${encodeURIComponent(barcode)}`;

export const getBarcodeShortUrl = (tokenOrUrl) => {
  if (!tokenOrUrl) return "";
  if (tokenOrUrl.startsWith("http://") || tokenOrUrl.startsWith("https://")) return tokenOrUrl;
  return `${getBackendOrigin()}/s/${encodeURIComponent(tokenOrUrl)}`;
};

const buildAuthenticatedHeaders = () => {
  const token = getToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
};

const extractFilename = (url, fallbackName) => {
  try {
    const parsed = new URL(resolveBarcodeUrl(url));
    const pathname = parsed.pathname || "";
    const parts = pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || fallbackName;
  } catch (error) {
    return fallbackName;
  }
};

export const fetchBarcodeBlob = async (url) => {
  const response = await fetch(resolveBarcodeUrl(url), {
    method: "GET",
    headers: buildAuthenticatedHeaders(),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `Request failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const contentType = response.headers.get("content-type") || blob.type || "";
  const contentDisposition = response.headers.get("content-disposition") || "";
  const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);

  return {
    blob,
    contentType,
    fileName: filenameMatch?.[1] || extractFilename(url, "download"),
  };
};

export const openBarcodeProtectedResource = async (url) => {
  const { blob } = await fetchBarcodeBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return objectUrl;
};

export const downloadBarcodeProtectedResource = async (url, fallbackName = "download") => {
  const { blob, fileName } = await fetchBarcodeBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName || fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

export const getNamingConfigs = () => {
  logBarcodeRequest("GET", "/barcodes/naming-configs");
  return apiClient.get("/barcodes/naming-configs", barcodeRequestConfig);
};

export const createNamingConfig = (payload) => {
  logBarcodeRequest("POST", "/barcodes/naming-configs", payload);
  return apiClient.post("/barcodes/naming-configs", payload, barcodeRequestConfig);
};

export const updateNamingConfig = (id, payload) => {
  logBarcodeRequest("PUT", `/barcodes/naming-configs/${id}`, payload);
  return apiClient.put(`/barcodes/naming-configs/${id}`, payload, barcodeRequestConfig);
};

export const deleteNamingConfig = (id) => {
  logBarcodeRequest("DELETE", `/barcodes/naming-configs/${id}`);
  return apiClient.delete(`/barcodes/naming-configs/${id}`, barcodeRequestConfig);
};

export const getMatchingAssetsForNaming = (
  institutionName,
  categoryCode,
  wardId,
  search = "",
  filters = {}
) => {
  const institutionFilters = Array.isArray(filters.institutionFilters)
    ? filters.institutionFilters
    : [];
  const params = Object.fromEntries(
    Object.entries({
      institutionName,
      categoryCode,
      wardId,
      search,
      ...filters,
      reviewed: filters.reviewed ?? true,
    }).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
  );
  if (institutionFilters.length > 0) {
    params.institutionFilters = JSON.stringify(institutionFilters);
  }
  logBarcodeRequest("GET", "/barcodes/naming-configs/matching-assets", params);
  return apiClient.get("/barcodes/naming-configs/matching-assets", {
    ...barcodeRequestConfig,
    params,
  });
};

export const getNamingInstitutionOptions = (search, categoryCode, wardIds, reviewed = true) => {
  const params = Object.fromEntries(
    Object.entries({ search, categoryCode, reviewed })
      .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
  );
  if (wardIds && wardIds.length > 0) params.wardIds = wardIds.join(',');
  logBarcodeRequest("GET", "/barcodes/naming-configs/institution-options", params);
  return apiClient.get("/barcodes/naming-configs/institution-options", {
    ...barcodeRequestConfig,
    params,
  });
};

export const getNamingConfigSummary = () => {
  logBarcodeRequest("GET", "/barcodes/naming-configs/summary");
  return apiClient.get("/barcodes/naming-configs/summary", barcodeRequestConfig);
};
